import { useState, useEffect } from 'react';
import api from '../services/api';
import { ShippingOrder } from '../types';

export default function TrackingPage() {
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('in_transit');
  const [selectedOrder, setSelectedOrder] = useState<ShippingOrder | null>(null);
  const [showDeliveredModal, setShowDeliveredModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [deliveredTo, setDeliveredTo] = useState('');
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [trackingDetail, setTrackingDetail] = useState<any>(null);

  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      const data = await api.getShippedOrders(params);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDeliveredModal = (order: ShippingOrder) => {
    setSelectedOrder(order);
    setDeliveredTo('');
    setShowDeliveredModal(true);
  };

  const handleMarkDelivered = async () => {
    if (!selectedOrder) return;
    setProcessing(true);
    try {
      await api.markDelivered({
        order_id: selectedOrder.id,
        delivered_to: deliveredTo || undefined,
      });
      setShowDeliveredModal(false);
      setTrackingDetail(null);
      loadOrders();
    } catch (error) {
      console.error('Failed to mark delivered:', error);
      alert(error instanceof Error ? error.message : 'Failed to mark delivered');
    } finally {
      setProcessing(false);
    }
  };

  const handleSyncTracking = async (order: ShippingOrder) => {
    setSyncingId(order.id);
    try {
      await api.syncOrderTracking(order.id);
      await loadOrders();
      // Refresh detail if this order is selected
      if (trackingDetail?.id === order.id) {
        selectOrder(order);
      }
    } catch (error: any) {
      console.error('Failed to sync tracking:', error);
      alert(error.message || 'Failed to sync tracking');
    } finally {
      setSyncingId(null);
    }
  };

  const selectOrder = (order: ShippingOrder) => {
    const events = parseJsonField((order as any).freight_tracking_events);
    const docs = parseJsonField((order as any).freight_documents);
    setTrackingDetail({
      ...order,
      trackingEvents: events,
      documents: docs,
    });
  };

  const parseJsonField = (val: any): any[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { return []; }
    }
    return [];
  };

  const getStatusBadge = (order: ShippingOrder) => {
    const fvStatus = (order as any).freight_tracking_status;
    if (order.delivered_at || fvStatus === 'delivered') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-900 text-green-300">Delivered</span>;
    }
    if (fvStatus === 'in_transit') {
      return <span className="px-2 py-1 text-xs rounded-full bg-indigo-900 text-indigo-300">In Transit</span>;
    }
    if (fvStatus === 'confirmed') {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-300">Booked</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-300">Shipped</span>;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Shipment Tracking</h1>
          <p className="text-gray-400 mt-1">Track shipped orders and mark deliveries</p>
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="all">All Shipped</option>
          </select>
          <button
            onClick={loadOrders}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Shipments Found</h3>
          <p className="text-gray-400">
            {filter === 'in_transit'
              ? 'There are no shipments currently in transit.'
              : filter === 'delivered'
              ? 'No delivered shipments found.'
              : 'No shipped orders found.'}
          </p>
        </div>
      ) : (
        <div className="flex gap-6">
          {/* Order list */}
          <div className={`${trackingDetail ? 'w-1/2' : 'w-full'} transition-all`}>
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Carrier</th>
                      {!trackingDetail && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tracking</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => selectOrder(order)}
                        className={`cursor-pointer transition-colors ${
                          trackingDetail?.id === order.id ? 'bg-gray-700' : 'hover:bg-gray-750'
                        }`}
                      >
                        <td className="px-4 py-4">
                          <div className="text-white font-medium">{order.order_number}</div>
                          <div className="text-gray-400 text-sm">{order.dealer_company}</div>
                          <div className="text-gray-500 text-xs">
                            {order.ship_to_city}, {order.ship_to_state}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-white">{order.carrier_name || (order as any).selected_carrier_name || '-'}</div>
                          {(order.bol_number || (order as any).freight_bol_number) && (
                            <div className="text-gray-400 text-sm">BOL: {order.bol_number || (order as any).freight_bol_number}</div>
                          )}
                        </td>
                        {!trackingDetail && (
                          <td className="px-4 py-4">
                            {order.tracking_number ? (
                              <div className="text-indigo-400 font-mono text-sm">{order.tracking_number}</div>
                            ) : order.pro_number || (order as any).freight_pro_number ? (
                              <div className="text-gray-300 font-mono text-sm">PRO: {order.pro_number || (order as any).freight_pro_number}</div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-4">{getStatusBadge(order)}</td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSyncTracking(order); }}
                              disabled={syncingId === order.id}
                              className="p-1.5 text-gray-400 hover:text-white transition-colors"
                              title="Sync Tracking"
                            >
                              <svg className={`w-4 h-4 ${syncingId === order.id ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            {!order.delivered_at && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openDeliveredModal(order); }}
                                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              >
                                Delivered
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tracking detail panel */}
          {trackingDetail && (
            <div className="w-1/2">
              <div className="bg-gray-800 rounded-xl p-6 sticky top-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{trackingDetail.order_number}</h3>
                    <p className="text-gray-400 text-sm">{trackingDetail.dealer_company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(trackingDetail)}
                    <button
                      onClick={() => setTrackingDetail(null)}
                      className="p-1 text-gray-400 hover:text-white"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Delivery info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Shipped</p>
                    <p className="text-white text-sm">{formatDate(trackingDetail.shipped_at || trackingDetail.freight_booked_at)}</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Est. Delivery</p>
                    <p className="text-white text-sm">{formatDate(trackingDetail.estimated_delivery || trackingDetail.freight_delivery_estimate)}</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">Carrier</p>
                    <p className="text-white text-sm">{trackingDetail.carrier_name || trackingDetail.selected_carrier_name || '-'}</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-gray-500 text-xs uppercase mb-1">PRO / BOL</p>
                    <p className="text-white text-sm font-mono">
                      {trackingDetail.pro_number || trackingDetail.freight_pro_number || trackingDetail.freight_bol_number || '-'}
                    </p>
                  </div>
                </div>

                {/* Tracking timeline */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-300 uppercase">Tracking Events</h4>
                    <button
                      onClick={() => handleSyncTracking(trackingDetail)}
                      disabled={syncingId === trackingDetail.id}
                      className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                    >
                      <svg className={`w-3 h-3 ${syncingId === trackingDetail.id ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                  {trackingDetail.trackingEvents.length === 0 ? (
                    <div className="bg-gray-900 rounded-lg p-4 text-center">
                      <p className="text-gray-500 text-sm">No tracking events yet</p>
                      <p className="text-gray-600 text-xs mt-1">Click Refresh to sync from FreightView</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {trackingDetail.trackingEvents.map((event: any, idx: number) => (
                        <div key={idx} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-indigo-500' : 'bg-gray-600'}`} />
                            {idx < trackingDetail.trackingEvents.length - 1 && (
                              <div className="w-0.5 h-full bg-gray-700 min-h-[24px]" />
                            )}
                          </div>
                          <div className="pb-4">
                            <p className="text-white text-sm">{event.status || event.description}</p>
                            {event.location && (
                              <p className="text-gray-400 text-xs">{event.location}</p>
                            )}
                            <p className="text-gray-500 text-xs">
                              {event.date}{event.time ? ` ${event.time}` : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Documents */}
                {trackingDetail.documents.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-300 uppercase mb-3">Documents</h4>
                    <div className="space-y-2">
                      {trackingDetail.documents.map((doc: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-900 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-gray-300 text-sm">{doc.name || doc.type || 'Document'}</span>
                          </div>
                          {doc.url && (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-400 hover:text-indigo-300"
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Last synced */}
                {trackingDetail.freight_tracking_synced_at && (
                  <p className="text-gray-600 text-xs mt-4">
                    Last synced: {new Date(trackingDetail.freight_tracking_synced_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mark Delivered Modal */}
      {showDeliveredModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Mark as Delivered</h3>
            <p className="text-gray-400 mb-4">Order: {selectedOrder.order_number}</p>

            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <p className="text-gray-300 text-sm">
                <strong>Ship To:</strong> {selectedOrder.ship_to_company}
              </p>
              <p className="text-gray-400 text-sm">
                {selectedOrder.ship_to_address}<br />
                {selectedOrder.ship_to_city}, {selectedOrder.ship_to_state} {selectedOrder.ship_to_zip}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Received By (optional)</label>
              <input
                type="text"
                value={deliveredTo}
                onChange={(e) => setDeliveredTo(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Name of person who received"
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeliveredModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkDelivered}
                disabled={processing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {processing ? 'Processing...' : 'Confirm Delivered'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
